<template>
    <b-container fluid>
        <b-row>
            <b-col></b-col>
            <b-col>
                <b-alert
                    variant="danger"
                    :show="hasError === true">
                    <div class="wrapper">
                        <div class="left">
                            <icon name="exclamation" scale="3"></icon>
                        </div>
                        <div class="right">
                            <span><strong>{{ $t('wizard.error.title') }}</strong></span>
                            <p>{{ this.errorMsg }}</p>
                        </div>
                    </div>
                </b-alert>

                <b-form novalidate v-show="hasError === false">
                    <b-card border-variant="secondary"
                            header-bg-variant="secondary"
                            header-text-variant="white"
                            :header="$t('wizard.title')"
                            class="text-center">
                        <b-form-group v-if="inputLimits">
                            <template slot="label">
                                <b-row>
                                    <b-col cols="9" class="text-left">{{ $t('wizard.fields.name') }}: *</b-col>
                                    <b-col cols="3" class="text-right">
                                        <small>
                                            <b-badge>{{projectData.name.length}} / {{inputLimits.projectName}}</b-badge>
                                        </small>
                                    </b-col>
                                </b-row>
                            </template>
                            <b-form-input
                                id="projectName"
                                name="projectRoomName"
                                type="text"
                                v-model="projectData.name"
                                :maxlength="inputLimits.projectName"
                                v-validate="'required'"
                                :placeholder="$t('wizard.placeholder.name')">
                            </b-form-input>
                            <span v-show="veeErrors.has('projectRoomName')" style="color: red;" class="small float-left">{{ $t('wizard.validation.roomName') }}</span>
                        </b-form-group>
                        <b-form-group v-if="inputLimits">
                            <template slot="label">
                                <b-row>
                                    <b-col cols="9" class="text-left">{{ $t('wizard.fields.description') }}:</b-col>
                                    <b-col cols="3" class="text-right">
                                        <small>
                                            <b-badge>{{projectData.description.length}} / {{inputLimits.projectDescription}}</b-badge>
                                        </small>
                                    </b-col>
                                </b-row>
                            </template>
                            <b-form-textarea
                                id="projectDescription"
                                no-resize
                                v-model="projectData.description"
                                :maxlength="inputLimits.projectDescription"
                                :rows="2"
                                :placeholder="$t('wizard.placeholder.description')">
                            </b-form-textarea>
                        </b-form-group>
                        <b-form-group class="text-left">
                            <label><strong>{{ $t('wizard.fields.root') }}</strong></label>
                            <div class="form-control pt-3 bg-light wrapper">
                                <div class="left">
                                    <icon name="folder" scale="3" v-if="this.rawData && this.rawData.type.toUpperCase() === 'REGISTER'"></icon>
                                    <icon name="folder-open" scale="3" v-else></icon>
                                </div>
                                <div class="right">
                                    <span><strong>{{ rawData ? rawData.title : '' }}</strong></span>
                                    <p>{{ rawData ? rawData.info : '' }} (<em>{{ rawData ? rawData.id : '' }}</em>)</p>
                                </div>
                            </div>
                        </b-form-group>
                        <b-form-group>
                            <label class="float-left"><strong>{{ $t('wizard.fields.queryDepth') }}</strong></label>
                            <b-form-input
                                id="documentQueryDepth"
                                name="documentQueryDepth"
                                type="number"
                                v-model="projectData.documentQueryDepth"
                                v-validate="'required|numeric|min_value:1|max_value:10'"
                                min="1"
                                max="10">
                            </b-form-input>
                            <span v-show="veeErrors.has('documentQueryDepth')" style="color: red;" class="small float-left">{{ $t('wizard.validation.queryDepth') }}</span>
                        </b-form-group>
                        <hr>
                        <b-button type="button" class="mr-1"
                                  variant="outline-danger" 
                                  @click="goToDashboard()">
                                  {{ $t('wizard.cancel') }}
                        </b-button>
                        <b-button type="button" 
                                  :disabled="veeErrors.count() > 0" 
                                  variant="outline-success" 
                                  @click="createProject()">
                                  {{ $t('wizard.create') }}
                        </b-button>
                    </b-card>
                </b-form>

                <b-card no-body class="mb-1" v-if="hasError === false && duplicateProjects.length">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button variant="outline-primary" block v-b-toggle.accordion>
                            <b-row>
                                <b-col>
                                    <span style="font-size: 95%" v-text="$t('wizard.duplicateProjectsLabel')"></span>
                                </b-col>
                                <b-col class="text-right">
                                    <icon class="arrow-down mt-1" name="caret-down"></icon>
                                    <icon class="arrow-up mt-1" name="caret-up"></icon>
                                </b-col>
                            </b-row>
                        </b-button>
                    </b-card-header>
                    <b-collapse id="accordion" accordion="accordion" role="tabpanel">
                        <b-card-body>
                            <b-row class="text-center">
                                <b-col cols="6">
                                    <span v-text="$t('wizard.projectName')"></span>
                                </b-col>
                                <b-col cols="6">
                                    <span v-text="$t('wizard.owners')"></span>
                                </b-col>
                            </b-row>
                            <hr class="mb-2 mt-2" style="border-top-width: 3px;">
                            <div v-for="project in duplicateProjects" :key="project.id">
                                <b-row>
                                    <b-col cols="6">
                                        <small v-text="project.name"></small>
                                    </b-col>
                                    <b-col cols="6">
                                        <small v-for="(owner, index) in project.owners" 
                                               :key="index" 
                                               class="mr-1">
                                            <b-badge v-text="owner"></b-badge>
                                        </small>
                                    </b-col>
                                </b-row>
                                <hr class="mb-1 mt-1">
                            </div>
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </b-col>
            <b-col></b-col>
        </b-row>
    </b-container>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import router from '@/router';
import { mapState } from 'vuex';
import i18n from '@/i18n.bootstrap';
import projectsApi from '../../api/projects';

export default {
    name: 'project-room-creator',

    data() {
        return {
            hasError: null,
            errorMsg: null,
            errorType: null,

            enaioSessionGuid: null,
            enaioObjectId: null,

            rawData: {
                type: 'FOLDER',
                title: '',
                info: ''
            },

            projectData: {
                name: '',
                description: '',
                enaioRoot: '',
                documentQueryDepth: 2
            },

            duplicateProjects: []
        }
    },

    computed: {
        ...mapState('system', ['inputLimits'])
    },

    created() {
        if (this.$route.params.objectid) {
			this.enaioObjectId = this.projectData.enaioRoot = this.$route.params.objectid;
		}

		if (this.$route.params.session) {
			this.enaioSessionGuid = this.$route.params.session;
        }

        // load list of duplicate projects
        projectsApi.loadDuplicateProjects(this.enaioObjectId)
            .then((res) => { this.duplicateProjects = res.data.projects })
            .catch((err) => { 
                console.log(err);
                toastr.error(i18n.t('wizard.error.duplicateProjects'));
            });

        let url = `/colab/epr/documents/${this.enaioObjectId}`;
        axios.get(url, {
            headers: {
                "Authorization": 'ENAIO-SESSION ' + this.enaioSessionGuid
            }
        }).then(response => {
            if (response && response.data) {
                this.hasError = false;

                this.rawData = response.data;
                
                this.projectData.name = this.rawData.title;
                this.projectData.description = this.projectData.name;
            } else {
                this.hasError = true;
            }
        }, error => {
            this.hasError = true;
            this.errorType = error.response.data.type;
            if (this.errorType === 'OBJECT_NOT_FOUND') {
                this.errorMsg = i18n.t('wizard.error.objectNotFound', {id: this.enaioObjectId});
            } else if(this.errorType === 'PROJECT_ROOM_ROOT_OBJECT_INVALID') {
                this.errorMsg = i18n.t('wizard.error.onlyFromFolder');
            } else {
                this.errorMsg = i18n.t('wizard.error.message');
            }
        });

        this.$store.dispatch('system/loadInputLimits');
    },

    methods: {
        createProject() {
            this.$validator.validateAll().then(result => {
                if (result) {
                    let url = '/colab/epr/projects';
                    axios.post(url, this.projectData, {
                        headers: {
                            "Authorization": 'ENAIO-SESSION ' + this.enaioSessionGuid
                        }
                    }).then(response => {
                        // router.push('/projects/' + response.data.id);
                        const projectId = response.data.id;
                        this.$router.push({name: 'project-home', params: { projectId, membership: 'true' }});
                      },error => {
                          if (error.response.data[0].message === 'DMS_SERVICE_ERROR') {
                            toastr.error(i18n.t('general.DMS_SERVICE_ERROR'));
                            console.log('DMS service error', {...error});
                            reject(error);
                          }
                        }
                    );
                }
            });
        },

        goToDashboard() {
            this.$router.push({ name: 'dashboard' })
        }
    }
}
</script>

<style scoped>
.wrapper {
  display: flex;
}

.left {
  flex: 0 0 15%;
}

.right {
  flex: 1;
}

.btn.collapsed .arrow-up,
.btn:not(.collapsed) .arrow-down {
    display: none;
}
</style>
